/* Find your fit - the liquid panel's behaviour.
 *
 * Two jobs: reveal the step cards as the section scrolls in, and track the pointer so the highlight on the
 * panel drifts with it. The pointer motion is the only part that reads as "liquid" - the material itself is
 * CSS, and it needs this script only for the light that moves across it.
 *
 * Two deliberate constraints:
 *
 * 1. The reveal animates transform ONLY. An opacity fade would look nicer on its own, but a backdrop-filter
 *    element cannot be faded: opacity below 1 creates a backdrop root, which stops the element sampling what
 *    is behind it. Animating opacity on an ancestor does the same. So the cards rise rather than fade, and
 *    the glass keeps working the whole way.
 *
 * 2. The pre-reveal offset is applied BY THIS SCRIPT, not by the stylesheet. If the script never runs, the
 *    cards are simply at rest instead of stuck 16px low - the failure mode is a missing animation, not a
 *    misaligned section.
 *
 * The script is loaded from the section itself, so it only ships on pages that render the section, and
 * nothing here can break the theme's other scripts.
 */
(() => {
  if (window.__msFitLiquid) return;
  window.__msFitLiquid = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const shift = (panel, x, y) => {
    panel.style.setProperty('--ms-fit-x', x);
    panel.style.setProperty('--ms-fit-y', y);
  };

  const trackPointer = (panel) => {
    if (!finePointer()) return;
    panel.addEventListener('pointermove', (event) => {
      const rect = panel.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3);
      const y = ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3);
      shift(panel, x, y);
    });
    panel.addEventListener('pointerleave', () => shift(panel, '0', '0'));
  };

  const reveal = (panel) => {
    const steps = Array.from(panel.querySelectorAll('.ms-fit__step'));
    if (!steps.length) return;

    if (reduceMotion() || !('IntersectionObserver' in window)) {
      panel.classList.add('is-revealed');
      return;
    }

    steps.forEach((step) => { step.style.transform = 'translateY(16px)'; });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        panel.classList.add('is-revealed');
        steps.forEach((step) => { step.style.removeProperty('transform'); });
        observer.disconnect();
      });
    }, { threshold: 0.25 });

    observer.observe(panel);
  };

  const init = () => {
    document.querySelectorAll('.ms-fit').forEach((panel) => {
      if (panel.dataset.msFitInit === '1') return;
      panel.dataset.msFitInit = '1';
      reveal(panel);
      trackPointer(panel);
    });
  };

  /* Run now, and again on load and on Shopify's section events. The section can be replaced after this
     script runs - something in this storefront re-renders it - and a one-shot init then leaves the new copy
     unrevealed. init() is idempotent: the observer disconnects and re-adding the class is a no-op. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
